import React, { memo, useEffect, useRef } from 'react';

import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { GLOBAL_PAGE_FORGE } from '@confluence/named-routes/entry-points/routes.ts';
import { expVal } from '@confluence/feature-experiments';

import { FORGE_MODULE_GLOBAL_PAGE } from '../../ForgeModuleType.ts';
import { LazyForgeUIExtensions } from '../../lazy.ts';
import { getAppId } from '../../utils/appData.ts';
import { ForgeUIExtensionsExperienceTrackerSupport } from '../../ForgeUIExtensionsExperienceTrackerSupport.ts';

import { ForgeKeyboardShortcut } from './ForgeKeyboardShortcut.tsx';

export const ForgeGlobalKeyboardShortcuts: React.MemoExoticComponent<() => React.JSX.Element> =
	memo((): React.JSX.Element => {
		const { push } = useRouteActions();
		const experienceStarted = useRef(false);
		const experienceResolved = useRef(false);

		useEffect(() => {
			return () => {
				if (experienceStarted.current && !experienceResolved.current) {
					ForgeUIExtensionsExperienceTrackerSupport.abort(
						'ForgeGlobalKeyboardShortcuts unmounted before experience resolved',
					);
				}
			};
		}, []);

		return (
			<LazyForgeUIExtensions
				moduleType={FORGE_MODULE_GLOBAL_PAGE}
				render={(forgeModules, loading, error) => {
					if (loading) return null;

					const extensionData = { extensionType: 'keyboard-shortcut' };

					if (error) {
						if (
							!experienceStarted.current &&
							ForgeUIExtensionsExperienceTrackerSupport.isTrackable(extensionData) &&
							expVal('confluence_enable_forge_ui_extensions_metrics', 'isEnabled', false)
						) {
							ForgeUIExtensionsExperienceTrackerSupport.start(extensionData);
							experienceStarted.current = true;
						}
						if (experienceStarted.current && !experienceResolved.current) {
							ForgeUIExtensionsExperienceTrackerSupport.fail(error);
							experienceResolved.current = true;
						}
						if (!isErrorMarkedAsHandled(error)) {
							return <ErrorDisplay error={error} />;
						}
						return null;
					}

					if (
						!experienceStarted.current &&
						ForgeUIExtensionsExperienceTrackerSupport.isTrackable(extensionData) &&
						expVal('confluence_enable_forge_ui_extensions_metrics', 'isEnabled', false)
					) {
						ForgeUIExtensionsExperienceTrackerSupport.start(extensionData);
						experienceStarted.current = true;
					}
					if (experienceStarted.current && !experienceResolved.current) {
						ForgeUIExtensionsExperienceTrackerSupport.succeed();
						experienceResolved.current = true;
					}

					return forgeModules.map((module) => (
						<ForgeKeyboardShortcut
							key={module.id}
							module={module}
							action={() =>
								push(
									GLOBAL_PAGE_FORGE.toUrl({
										appId: getAppId(module),
										envId: module.environmentId,
										forgeManifestRoute: module.properties.route,
									}),
								)
							}
						/>
					));
				}}
			/>
		);
	});
